import type { OngoingLoadRoute } from '@numeo/types-core'
import { SectionLabel } from './ReviewNotice'

/** The place a stop is, as the drawer names it: its city and state. */
export const placeOf = (stop: OngoingLoadRoute | undefined): string | null => {
    const location = stop?.location as { city?: string; state?: string } | undefined
    const place = [location?.city, location?.state].filter(Boolean).join(', ')
    return place || null
}

/** One field of a document card: its label and value, a dash when the load has none. */
interface DocumentField {
    label: string
    value: string | null | undefined
}

interface DocumentCardProps {
    /** The document's name as the card heads it ("Rate confirmation"); drawn in capitals. */
    title: string
    /** The customer the document is from (the broker), under the title. */
    customer: string | null | undefined
    fields: DocumentField[]
    /** Where the document came from, under the card's SOURCE label. */
    source: string
}

/**
 * A document as the review drawer opens it (v17 `openDoc`): a grey card headed by the document's name and its customer, a rule, and its
 * fields in two columns (label, value), then where it came from. Read only: a change is made in the review, never here.
 */
export const DocumentCard = ({ title, customer, fields, source }: DocumentCardProps) => (
    <>
        <div className="rounded-[10px] border border-border bg-surface-secondary p-6">
            <h2 className="text-xl font-bold uppercase tracking-wide text-foreground">{title}</h2>
            <p className="mt-1 text-sm text-foreground-muted">{customer || '—'}</p>
            <hr className="my-6 border-0 border-t border-border" />
            <dl className="grid grid-cols-2 gap-2.5 text-[13px]">
                {fields.map((field) => (
                    <div key={field.label} className="contents">
                        <dt className="text-foreground-muted">{field.label}</dt>
                        <dd className="m-0 text-foreground">{field.value || '—'}</dd>
                    </div>
                ))}
            </dl>
        </div>
        <SectionLabel>Source</SectionLabel>
        <p className="text-sm text-foreground">{source}</p>
    </>
)
